import { useState } from "react";

import { View } from "react-native";
import { Divider, HelperText } from "react-native-paper";
import { useTailwind } from "tailwind-rn";

import DeliveryOptions from "./DeliveryOptions";
import DeliverySchedules from "./DeliverySchedules";
import LoadingView from "../../../../components/LoadingView";
import DisabledView from "../../components/DisabledView";
import SwitchWithText from "../../components/SwitchWithText";
import useDeliveryFulfillments from "../../contexts/DeliveryFulfillmentsContext";
import {
  deliveryFulfillmentsRoutes,
  DeliveryFulfillmentsScreenProps,
} from "../../navigation/types";
import { FulfillmentDispatchType } from "../../types";

type Props = DeliveryFulfillmentsScreenProps<
  typeof deliveryFulfillmentsRoutes.automaticFulfillment
>;
export default function AutomaticFulfillmentScreen({ route }: Props) {
  const tailwind = useTailwind();

  const type = route.params.type;

  const description = getDescription(type);

  const { fulfillmentOptions, updateAutomaticFulfillment } =
    useDeliveryFulfillments();

  const filteredOptions = fulfillmentOptions.filter(
    (fo) => fo.dispatch_type === type
  );

  const [optimisticEnabled, setOptimisticEnabled] = useState<boolean | null>(
    null
  );

  const computedEnabled =
    optimisticEnabled ?? filteredOptions.some((fo) => fo.published);

  async function handleToggle(nextEnabled: boolean) {
    setOptimisticEnabled(nextEnabled);
    try {
      await updateAutomaticFulfillment(type, nextEnabled);
    } finally {
      setOptimisticEnabled(null);
    }
  }

  return (
    <View style={tailwind("m-4 gap-4 flex-1")}>
      <SwitchWithText
        title={computedEnabled ? "Desactivar" : "Activar"}
        details={`Pulsa para ${
          computedEnabled ? "desactivar" : "activar"
        } este método de envío`}
        enabled={computedEnabled}
        onChange={handleToggle}
        loading={optimisticEnabled !== null}
      />

      <Divider />

      <HelperText type="info">{description}</HelperText>

      <Divider />

      {optimisticEnabled !== null ? (
        <LoadingView />
      ) : computedEnabled ? (
        <>
          <View style={tailwind("gap-8 flex-1")}>
            <DeliverySchedules type={type} />

            <DeliveryOptions filteredOptions={filteredOptions} />
          </View>
        </>
      ) : (
        <DisabledFulfillment type={type} />
      )}
    </View>
  );
}

function getDescription(type: FulfillmentDispatchType) {
  switch (type) {
    case "on_demand":
      return "Los pedidos con este método aparecerán como pendientes y, cuando los marques “listos para recoger”, un mensajero pasará de inmediato y entregará de inmediato.";
    case "scheduled":
      return "Los pedidos con este método aparecerán como pendientes y, cuando los marques “listos para recoger”, se retirarán en la próxima recolección diaria y saldrán a reparto al día siguiente tras pasar por el centro logístico.";
  }
}

function DisabledFulfillment({ type }: { type: FulfillmentDispatchType }) {
  const word = type === "on_demand" ? "directos" : "diferidos";
  const title = `¡Activa envíos ${word} para agilizar tus envíos!`;

  return <DisabledView icon="truck-fast" iconString="🚚📦" title={title} />;
}
